import BaseStore from 'bz-stores/mobx/base-store';
import useComputed from 'bz-utilities/hooks/computed';
import { action, computed, type IObservableArray, observable } from 'mobx';
import {
  createContext,
  type FC,
  memo,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { FeatureRequest, type LicenceModule, ModularFeaturesRequest } from 'tm3-core/api.dtos';
import ModuleEnum from 'tm3-core/licence';

interface IProps extends PropsWithChildren {
  authenticated: boolean;
}

export enum LicenceType {
  UserLicence = 'User',
  DiaryLicence = 'Diary',
  FixedLicence = 'Fixed'
}

export const LicenceTypeContext = createContext<string>([]);
export const FeatureFlagsContext = createContext<string[]>([]);
export const AllowedModulesContext = createContext<(module: ModuleEnum) => boolean>(() => false);
export const ModuleFeatureContext = createContext<LicenceModule[]>([]);
export const ModulesLoadedContext = createContext<boolean>(false);

export const useAllowed = () => {
  return useContext(AllowedModulesContext);
};
export const useModulesLoaded = () => useContext(ModulesLoadedContext);
class ObservableModuleFeatureStore {
  @observable modules: IObservableArray<LicenceModule> = observable.array([]);

  @computed
  get allowedModules() {
    const values = new Set<string>();
    this.modules.forEach((module) => {
      if (module?.name) values.add(module.name);
      if (module?.id) {
        values.add(module.id);
        const mapped = (ModuleEnum as Record<string, string>)[module.id];
        if (mapped) values.add(mapped);
      }
    });
    return [...values];
  }

  load(data: LicenceModule[]) {
    this.modules.replace(data);
  }
  @action.bound clear() {
    this.modules.clear();
  }
  @action.bound hasAllowedModule(module: ModuleEnum) {
    return this.allowedModules.includes(module);
  }
}

export const ModuleFeatureObservable = new ObservableModuleFeatureStore();

const ModuleFeatureProvider: FC<IProps> = memo(({ children, authenticated }) => {
  const [flags, setFlags] = useState<string[]>([]);
  const [licenceType, setLicenceType] = useState<string>('');
  const [modularFeatures, setModularFeatures] = useState<LicenceModule[]>([]);
  const [modulesLoaded, setModulesLoaded] = useState<boolean>(false);

  const allowedModules = useMemo(() => {
    const values = new Set<string>();
    modularFeatures.forEach((module) => {
      if (module?.name) values.add(module.name);
      if (module?.id) {
        values.add(module.id);
        const mapped = (ModuleEnum as Record<string, string>)[module.id];
        if (mapped) values.add(mapped);
      }
    });
    const result = [...values];
    return result;
  }, [modularFeatures]);

  const hasAllowedModule = useCallback(
    (module: ModuleEnum) => allowedModules.includes(module),
    [allowedModules]
  );

  const onLoadFlags = useCallback(async () => {
    const res = await BaseStore.defaultClient.get(new FeatureRequest());
    return Object.keys(res.result).filter((key) => res.result[key] === 'TRUE');
  }, []);

  const onLoadModularFeatures = useCallback(async () => {
    const res = await BaseStore.defaultClient.get(new ModularFeaturesRequest());
    return res.result;
  }, []);

  useEffect(() => {
    let active = true;

    setModulesLoaded(false);
    setFlags([]);
    setModularFeatures([]);
    setLicenceType('');
    ModuleFeatureObservable.clear();

    if (authenticated) {
      void onLoadFlags().then((nextFlags) => {
        if (active) setFlags(nextFlags);
      });
      void onLoadModularFeatures().then((nextFeatures) => {
        if (!active) return;
        setModularFeatures(nextFeatures.modules);
        setLicenceType(nextFeatures.type);
        ModuleFeatureObservable.load(nextFeatures.modules);
        setModulesLoaded(true);
      });
    }

    return () => {
      active = false;
    };
  }, [authenticated, onLoadFlags, onLoadModularFeatures]);

  return (
    <ModulesLoadedContext.Provider value={modulesLoaded}>
      <ModuleFeatureContext.Provider value={modularFeatures}>
        <LicenceTypeContext.Provider value={licenceType}>
          <AllowedModulesContext.Provider value={hasAllowedModule}>
            <FeatureFlagsContext.Provider value={flags}>{children}</FeatureFlagsContext.Provider>
          </AllowedModulesContext.Provider>
        </LicenceTypeContext.Provider>
      </ModuleFeatureContext.Provider>
    </ModulesLoadedContext.Provider>
  );
});

export default ModuleFeatureProvider;
